import type { Feature, FeatureCollection, Geometry, Point } from 'geojson'

export type NetworkProperties = {
  feature_id: string;
  element_type: string;
  name: string;
  tag: string;
  metadata_summary: unknown[];
  metadata: unknown;
};

export type NetworkPointFeature = Feature<Point, NetworkProperties>;

export type GeoJsonTrackPoint = {
  featureId: string;
  name: string;
  tag: string;
  metadataSummary: unknown[];
  metadata: unknown;

  /**
   * GeoJSON coordinate order:
   * [longitude, latitude]
   */
  coordinates: [number, number];

  /**
   * Preserve the complete original feature.
   */
  feature: NetworkPointFeature;
};

export type GeoJsonTrackSection = {
  railwayEdgeId: string;
  railwayEdgeName: string;
  edgeName: string;
  networkId: string;
  length: number;

  lineString: {
    type: "LineString";
    coordinates: [number, number][];
  };

  points: GeoJsonTrackPoint[];
};

export type GeoJsonNetworkAdapter = {
  id: string;
  edgesOfNetwork: GeoJsonTrackSection[];
};

/**
 * Calculates distance between two GeoJSON coordinates.
 *
 * Coordinates are supplied as:
 * [longitude, latitude]
 */
export const calculateDistanceInMetres = (
  first: [number, number],
  second: [number, number]
): number => {
  const earthRadius = 6_371_000;

  const [firstLongitude, firstLatitude] = first;
  const [secondLongitude, secondLatitude] = second;

  const toRadians = (value: number) =>
    (value * Math.PI) / 180;

  const latitudeDifference = toRadians(
    secondLatitude - firstLatitude
  );

  const longitudeDifference = toRadians(
    secondLongitude - firstLongitude
  );

  const firstLatitudeRadians = toRadians(firstLatitude);
  const secondLatitudeRadians = toRadians(secondLatitude);

  const haversine =
    Math.sin(latitudeDifference / 2) ** 2 +
    Math.cos(firstLatitudeRadians) *
      Math.cos(secondLatitudeRadians) *
      Math.sin(longitudeDifference / 2) ** 2;

  const angularDistance =
    2 *
    Math.atan2(
      Math.sqrt(haversine),
      Math.sqrt(1 - haversine)
    );

  return earthRadius * angularDistance;
};

/**
 * Calculates track length using the current order of the Point features.
 *
 * Important:
 * This assumes the Point features are already ordered along the track.
 */
export const calculateTrackLength = (
  coordinates: [number, number][]
): number => {
  if (coordinates.length < 2) {
    return 0;
  }

  return coordinates
    .slice(0, -1)
    .reduce((total, coordinate, index) => {
      const nextCoordinate = coordinates[index + 1];

      return (
        total +
        calculateDistanceInMetres(
          coordinate,
          nextCoordinate
        )
      );
    }, 0);
};

/**
 * Converts all GeoJSON Point features into one track section.
 *
 * LineString and MultiLineString features are ignored.
 */
export const createTrackSectionFromGeoJson = (
  geoJsonData: FeatureCollection<
    Geometry,
    NetworkProperties
  >,
  options?: {
    networkId?: string;
    trackId?: string;
    trackName?: string;
  }
): GeoJsonTrackSection | null => {
  const nodeFeatures = geoJsonData.features.filter(
    (feature): feature is NetworkPointFeature =>
      feature.geometry.type === "Point" &&
      feature.properties?.element_type === "node"
  );

  if (nodeFeatures.length === 0) {
    return null;
  }

  const points: any =
    nodeFeatures.map((feature) => ({
      featureId: feature.properties.feature_id,
      name: feature.properties.name,
      tag: feature.properties.tag,
      metadataSummary:
        feature.properties.metadata_summary,
      metadata: feature.properties.metadata,
      coordinates: feature.geometry.coordinates,
      feature,
    }));

  const coordinates = points.map(
    (point: any) => point.coordinates
  );

  const trackName =
    options?.trackName ?? "Selected Track";

  return {
    railwayEdgeId:
      options?.trackId ?? "geojson-track",
    railwayEdgeName: trackName,
    edgeName: trackName,
    networkId:
      options?.networkId ??
      "selected-online-network",
    length: calculateTrackLength(coordinates),

    lineString: {
      type: "LineString",
      coordinates,
    },

    points,
  };
};

export const buildValuesFromMetadata = (
  metadata: any,
  storedInputParams: any[]
): Record<string, any> => {
  if (
    !metadata ||
    typeof metadata !== "object" ||
    !Array.isArray(metadata.data) ||
    metadata.data.length === 0
  ) {
    return {};
  }

  const schemaFieldCodes = new Set(
    storedInputParams.flatMap((group: any) =>
      (group.parameters ?? []).map((parameter: any) => parameter.code)
    )
  );

  const metadataValues: Record<string, any> = {};

  const isUsableMetadataValue = (value: unknown) =>
    value !== undefined &&
    value !== null &&
    value !== "";

  metadata.data.forEach((item: any) => {
    if (!item?.key) return;

    const { key, value } = item;

    const isNestedObject =
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value);

    // First-level metadata
    if (!isNestedObject) {
      if (
        schemaFieldCodes.has(key) &&
        isUsableMetadataValue(value)
      ) {
        metadataValues[key] = value;
      }

      return;
    }

    // Second-level metadata
    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      if (
        schemaFieldCodes.has(nestedKey) &&
        isUsableMetadataValue(nestedValue)
      ) {
        metadataValues[nestedKey] = nestedValue;
      }
    });
  });

  return metadataValues;
};

export const buildValuesFromNetworkMetadata = (
  networkData: any,
  storedInputParams: any[]
): Record<string, any> => {
  const metadata = networkData?.metadata;

  if (
    !metadata ||
    typeof metadata !== "object" ||
    Array.isArray(metadata)
  ) {
    return {};
  }

  const schemaFieldCodes = new Set(
    storedInputParams.flatMap((group: any) =>
      (group.parameters ?? []).map(
        (parameter: any) => parameter.code
      )
    )
  );

  const metadataValues: Record<string, any> = {};

  const isUsableMetadataValue = (value: unknown) =>
    value !== undefined &&
    value !== null &&
    value !== "";

  Object.entries(metadata).forEach(([key, value]) => {
    const isNestedObject =
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value);

    // First-level metadata
    if (!isNestedObject) {
      if (
        schemaFieldCodes.has(key) &&
        isUsableMetadataValue(value)
      ) {
        metadataValues[key] = value;
      }

      return;
    }

    // Second-level metadata
    Object.entries(value).forEach(
      ([nestedKey, nestedValue]) => {
        if (
          schemaFieldCodes.has(nestedKey) &&
          isUsableMetadataValue(nestedValue)
        ) {
          metadataValues[nestedKey] = nestedValue;
        }
      }
    );
  });

  return metadataValues;
};

export const buildValuesForNetwork = (
  onlineNetworks: any,
  networkId: string | undefined,
  storedInputParams: any[]
): Record<string, any> => {
  if (!Array.isArray(onlineNetworks) || !networkId) {
    return {};
  }

  const matchedNetwork = onlineNetworks.find(
    (network: any) =>
      network?.network_uuid === networkId
  );

  if (!matchedNetwork) {
    return {};
  }

  return buildValuesFromNetworkMetadata(
    matchedNetwork,
    storedInputParams
  );
};